import { Close } from "@mui/icons-material";
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import classes from "./index.module.scss";
import EditTask from "./EditTask";
import TodoItem from "./TodoItem";
import { useTodoList } from "./hooks";
import { TodoItemObject } from "types/todolist";
import { EMAILID_COOKIE, MOCK_USER_COOKIE } from "constant";
import { Cookies } from "utils";
import moment from "moment-timezone";

interface IndexProps {
  open: boolean;
  onClose: () => void;
  mutateOverdue: () => void;
}

const Index: FC<IndexProps> = ({ open, onClose, mutateOverdue }) => {
  const tabList = ["Today", "My Tasks"];

  const [activeTab, setActiveTab] = useState(tabList[0]);
  const [openTask, setOpenTask] = useState(false);

  const { data: list, mutate } = useTodoList(
    moment.tz.guess(),
    activeTab === "Today" ? "today" : "mytask"
  );

  useTodoList(moment.tz.guess(), "mytask");

  const userEmail = Cookies.get(EMAILID_COOKIE);
  const mockUserEmail = Cookies.get(MOCK_USER_COOKIE);

  const assignToMe = useMemo(() => {
    return list && list?.length
      ? list?.filter(
          (f: TodoItemObject) =>
            (!f.assignedTo ||
              f.assignedTo === (userEmail?.split("@")?.[0] ?? mockUserEmail?.split("@")?.[0])) &&
            !f.completed
        )
      : [];
  }, [list, activeTab]);

  const assignToOthers = useMemo(() => {
    return list && list?.length
      ? list?.filter(
          (f: TodoItemObject) =>
            f.assignedTo &&
            f.assignedTo !== (userEmail?.split("@")?.[0] ?? mockUserEmail?.split("@")?.[0]) &&
            !f.completed
        )
      : [];
  }, [list, activeTab]);

  const doneTasks = useMemo(() => {
    return list && list?.length ? list?.filter((f: TodoItemObject) => f.completed) : [];
  }, [list, activeTab]);

  const notDoneTasks = useMemo(() => {
    return list && list?.length ? list?.filter((f: TodoItemObject) => !f.completed) : [];
  }, [list, activeTab]);

  const handleDialogClose = () => {
    onClose();
    handleCloseTask();
  };

  const handleTask = () => {
    setOpenTask(true);
  };
  const handleCloseTask = () => {
    setOpenTask(false);
  };

  useEffect(() => {
    handleCloseTask();
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(
      (list || []).filter((item: any) => !item.completed)?.length > 0 ? "Today" : "My Tasks"
    );
  }, []);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>My Todo List</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
        <DialogContent sx={{ padding: "0px 24px 20px 24px" }}>
          <div className={classes.tabWrapper}>
            {tabList.map((tab) => (
              <div
                key={tab}
                className={`${classes.tab} ${tab === activeTab ? classes.active : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </div>
            ))}
          </div>
          <div>
            <div className={classes.titleWrapper}>
              <div className={classes.title}>
                {activeTab === "Today" ? "Today's" : "All My"} Tasks
              </div>
              <Button
                variant="outlined"
                onClick={handleTask}
                sx={{ cursor: "pointer" }}
                size="small"
              >
                + Add Task
              </Button>
            </div>
            <div>
              {/* new task */}
              {openTask ? (
                <EditTask
                  type="create"
                  onClose={handleCloseTask}
                  mutate={mutate}
                  mutateOverdue={mutateOverdue}
                />
              ) : null}
              {
                // 0. if list item completed === true, show completed section
                // 1. if list item assignTo === current user / no assignee, show in myself section
                // 2. if list item assignTo !== current user, show in assign to others section
                // 3. if list without complete = false, then show 'No active tasks for today/my tasks'
              }
              {notDoneTasks?.length === 0 && !openTask ? (
                <div className={classes.noTaskWrapper}>
                  No active tasks
                  {activeTab === "Today" ? " for today" : ""}
                </div>
              ) : null}
              {/* myself task */}
              {assignToMe?.length !== 0 ? (
                <div className={classes.section}>
                  <p className={classes.sectionTitle}>Myself</p>
                  {activeTab === "Today" &&
                    assignToMe.map((i: TodoItemObject) => {
                      return (
                        <TodoItem
                          key={`${i.id}-${i.itemId ?? ""}`}
                          item={i}
                          onClose={handleCloseTask}
                          mutate={mutate}
                          tab={"today"}
                          mutateOverdue={mutateOverdue}
                        />
                      );
                    })}
                  {activeTab === "My Tasks" &&
                    assignToMe
                      ?.filter((f: TodoItemObject) => f.showInMyself)
                      ?.map((i: TodoItemObject) => (
                        <div key={`${i.id}-${i.itemId ?? ""}`}>
                          <TodoItem
                            item={i}
                            onClose={handleCloseTask}
                            mutate={mutate}
                            tab={"mytask"}
                            mutateOverdue={mutateOverdue}
                          />
                          {i.children && i.children?.length
                            ? i.children.map((child) => (
                                <TodoItem
                                  key={child.id}
                                  item={child}
                                  onClose={handleCloseTask}
                                  mutate={mutate}
                                  tab={"mytask"}
                                  mutateOverdue={mutateOverdue}
                                />
                              ))
                            : null}
                        </div>
                      ))}
                </div>
              ) : null}
              {/* others task */}
              {assignToOthers?.length !== 0 ? (
                <div className={classes.section}>
                  <p className={classes.sectionTitle}>Assigned to Others</p>
                  {activeTab === "Today" &&
                    assignToOthers.map((i: TodoItemObject) => (
                      <TodoItem
                        key={`${i.id}-${i.itemId ?? ""}`}
                        item={i}
                        onClose={handleCloseTask}
                        mutate={mutate}
                        tab="today"
                        mutateOverdue={mutateOverdue}
                      />
                    ))}
                  {activeTab === "My Tasks" &&
                    assignToOthers
                      ?.filter((f: TodoItemObject) => f.showInOther)
                      .map((i: TodoItemObject) => (
                        <div key={`${i.id}-${i.itemId ?? ""}`}>
                          <TodoItem
                            item={i}
                            onClose={handleCloseTask}
                            mutate={mutate}
                            tab={"mytask"}
                            mutateOverdue={mutateOverdue}
                          />
                          {i.children && i.children?.length
                            ? i.children.map((child) => (
                                <TodoItem
                                  key={child.id}
                                  item={child}
                                  onClose={handleCloseTask}
                                  mutate={mutate}
                                  tab={"mytask"}
                                  mutateOverdue={mutateOverdue}
                                />
                              ))
                            : null}
                        </div>
                      ))}
                </div>
              ) : null}
              {/* completed task */}
              {doneTasks?.length !== 0 ? (
                <div className={classes.section}>
                  <p className={classes.sectionTitle}>Completed</p>
                  {doneTasks.map((i: TodoItemObject) => (
                    <TodoItem
                      key={`${i.id}-${i.itemId ?? ""}`}
                      item={i}
                      onClose={handleCloseTask}
                      mutate={mutate}
                      tab="today"
                      mutateOverdue={mutateOverdue}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default Index;
